import React, { useState } from 'react';
import { useAppStore } from '@/store';
import { Modal, Select, CollapsiblePanel, Button, Tooltip } from './shared';
import type { SelectOption } from './shared';
import { ProfileSelector } from './Auth/ProfileSelector';
import { DataErasurePanel } from './Settings/DataErasurePanel';
import { FeedbackForm } from './Feedback/FeedbackForm';
import { FAQPanel } from './Help/FAQPanel';
import { GlossaryPanel } from './Help/GlossaryPanel';
import { PolicySearchPanel } from './PolicyDatabase/PolicySearchPanel';
import { PolicyConfigForm } from './PolicyConfig/PolicyConfigForm';
import { NotificationPreferencesPanel } from './Settings/NotificationPreferencesPanel';
import { CalendarSyncPanel } from './CalendarIntegration/CalendarSyncPanel';
import { LmsConnectPanel } from './LmsConnect/LmsConnectPanel';
import { SettingsDrawer, TabGroup } from 'calc-nav';
import { emptyState } from '@/data/demoDatasets';

export interface SettingsPanelProps {
  isOpen: boolean;
  onClose: () => void;
  onOpenAbout?: () => void;
}

export function SettingsPanel({ isOpen, onClose, onOpenAbout }: SettingsPanelProps): React.ReactElement {
  const settings = useAppStore((s) => s.settings);
  const updateSettings = useAppStore((s) => s.updateSettings);
  const policies = useAppStore((s) => s.policies);
  const courses = useAppStore((s) => s.courses);
  const importState = useAppStore((s) => s.importState);

  const [showProfileManager, setShowProfileManager] = useState(false);
  const [showErasure, setShowErasure] = useState(false);
  const [showPolicyEditor, setShowPolicyEditor] = useState(false);
  const [notifCourseId, setNotifCourseId] = useState('');

  const notifCourse = courses.find((c) => c.id === notifCourseId) ?? courses[0] ?? null;

  const themeOptions: SelectOption[] = [
    { value: 'light', label: 'Light' },
    { value: 'dark', label: 'Dark' },
    { value: 'system', label: 'System' },
  ];

  const policyOptions: SelectOption[] = [
    { value: '', label: '— None —' },
    ...policies.map((p) => ({ value: p.id, label: p.name })),
  ];

  return (
    <SettingsDrawer isOpen={isOpen} onClose={onClose} title="Settings">
      <div className="settings-panel">
        <TabGroup
          section="settings"
          stickyTop="0px"
          tabs={[
            {
              id: 'general',
              label: 'General',
              content: (
                <>
                  <div className="settings-panel__section">
                    <h4>Appearance</h4>
                    <Select
                      label="Theme"
                      options={themeOptions}
                      value={settings.theme}
                      onChange={(e) => updateSettings({ theme: e.target.value as 'light' | 'dark' | 'system' })}
                    />
                  </div>

                  <div className="settings-panel__section">
                    <h4>Defaults</h4>
                    <Select
                      label="Default Policy for New Courses"
                      options={policyOptions}
                      value={settings.defaultPolicyId ?? ''}
                      onChange={(e) => updateSettings({ defaultPolicyId: e.target.value || null })}
                    />
                  </div>

                  <div className="settings-panel__section">
                    <h4>Profiles</h4>
                    <button type="button" className="btn btn--secondary btn--sm" onClick={() => setShowProfileManager(true)}>
                      Manage Profiles
                    </button>
                  </div>

                  <div className="settings-panel__section">
                    <h4>Notifications</h4>
                    {courses.length === 0 ? (
                      <p className="settings-panel__hint">Add a course to configure per-course notifications.</p>
                    ) : (
                      <>
                        <Select
                          label="Course"
                          options={courses.map((c) => ({ value: c.id, label: `${c.code} — ${c.name}` }))}
                          value={notifCourse?.id ?? ''}
                          onChange={(e) => setNotifCourseId(e.target.value)}
                        />
                        {notifCourse && <NotificationPreferencesPanel course={notifCourse} />}
                      </>
                    )}
                  </div>
                </>
              ),
            },
            {
              id: 'integrations',
              label: 'Integrations',
              content: (
                <>
                  <CalendarSyncPanel />
                  <LmsConnectPanel />
                </>
              ),
            },
            {
              id: 'policies',
              label: 'Policies',
              content: (
                <CollapsiblePanel title="Policy Database" defaultOpen={false}>
                  <div className="settings-panel__policy-actions">
                    <Tooltip content="Create a new attendance policy from scratch or a template">
                      <Button variant="secondary" size="sm" onClick={() => setShowPolicyEditor(true)}>
                        + New Policy
                      </Button>
                    </Tooltip>
                  </div>
                  <PolicySearchPanel />
                </CollapsiblePanel>
              ),
            },
            {
              id: 'help',
              label: 'Help',
              content: (
                <>
                  <CollapsiblePanel title="Help & FAQ" defaultOpen={false}>
                    <FAQPanel />
                  </CollapsiblePanel>

                  <CollapsiblePanel title="Glossary" defaultOpen={false}>
                    <GlossaryPanel />
                  </CollapsiblePanel>
                </>
              ),
            },
            {
              id: 'data',
              label: 'Data',
              content: (
                <>
                  <CollapsiblePanel title="Send Feedback" defaultOpen={false}>
                    <FeedbackForm />
                  </CollapsiblePanel>

                  <div className="settings-panel__section">
                    <h4>Data & Privacy</h4>

                    <div className="settings-panel__demo-controls">
                      <label className="settings-panel__toggle">
                        <input
                          type="checkbox"
                          checked={settings.showLoadDemo !== false}
                          onChange={(e) => updateSettings({ showLoadDemo: e.target.checked })}
                        />
                        Show &ldquo;Load Demo&rdquo; option in header
                      </label>

                      <Tooltip content="Remove all loaded demo data and reset the store to its empty state">
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => importState(emptyState(settings))}
                        >
                          Clear demo data
                        </Button>
                      </Tooltip>
                    </div>

                  <button type="button" className="btn btn--danger btn--sm" onClick={() => setShowErasure(true)}>
                    Delete All Data
                  </button>
                </div>

                <hr />

                <div className="settings-panel__section">
                  <button
                    type="button"
                    className="btn btn--secondary btn--sm"
                    onClick={() => onOpenAbout?.()}
                  >
                    About
                  </button>
                </div>
                </>
              ),
            },
          ]}
        />
      </div>

      <ProfileSelector isOpen={showProfileManager} onClose={() => setShowProfileManager(false)} />
      <DataErasurePanel isOpen={showErasure} onClose={() => setShowErasure(false)} />
      <Modal isOpen={showPolicyEditor} onClose={() => setShowPolicyEditor(false)} title="Create / Edit Policy">
        <PolicyConfigForm
          onSave={() => setShowPolicyEditor(false)}
          onCancel={() => setShowPolicyEditor(false)}
        />
      </Modal>
    </SettingsDrawer>
  );
}
